<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title">
            Near Match Processing
          </div>
        </template>
      </EsHeader>
    </div>
    <es-loader v-if="loading" style="margin: 5rem"/>
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="0"
      style="padding: 5rem"
    />
    <div v-else class="content">
      <div style="padding-bottom: 1rem">
        <table class="left-right-padding">
          <thead>
            <tr>
              <th class="rule left-padding">
                Job Type
              </th>
              <th class="rule">
                Status
              </th>
              <th class="rule">
                Frequency Type
              </th>
              <th class="rule">
                Frequency Details
              </th>
              <th class="rule last-child">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
            <tr v-for="(schedule, index) in processSchedules" :key="index">
              <td class="left-padding no-margin">
                <span
                  class="tag source-tag no-margin"
                  style="font-size: 12px"
                >
                  {{ processSchedules[0].job.jobType }}
                  <!-- Should always be Cancel Near Matches -->
                </span>
              </td>
              <td>
                {{ processSchedules[0].processSchedule.status ? 'Active' : 'Inactive' }}
              </td>
              <td>
                {{ processSchedules[0].schedule.frequencyType }}
              </td>
              <td>
                {{ frequencyValue }}
              </td>
              <td>
                <a>
                  <span style="font-size: 17px">
                    <font-awesome-icon
                      :icon="['far', 'angle-double-right']"
                      size="lg"
                      title="View Near Match Details"
                      role="button"
                      tabindex="0"
                      @keypress="viewNearMatchProcessingDetails()"
                      @click="viewNearMatchProcessingDetails()"
                    />
                  </span>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleDoubleRight } from '@fortawesome/pro-regular-svg-icons';
import { faCog } from '@fortawesome/pro-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleRight,
  faCog,
  faPlus,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  created() {
    this.onLoad();
  },
  components: {
    EsException,
    EsHeader,
    EsLoader,
  },
  data() {
    return this.initialData();
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
      activeModules: 'appModules/activeModules',
      allModules: 'appModules/getAllModules',
      properties: 'propertiesModule/properties',
    }),
    formattedTime() {
      return (time) => {
        const date = new Date();
        date.setHours(Math.floor(time / 100));
        date.setMinutes(time % 100);
        return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
      };
    },
    frequencyValue() {
      if (this.processSchedules[0].schedule.frequencyType === 'Daily') {
        return this.formattedTime(this.processSchedules[0].schedule.startTime);
      } if (this.processSchedules[0].schedule.frequencyType === 'Weekly') {
        return `${this.processSchedules[0].schedule.frequencyDayOfWeek}
          ${this.formattedTime(this.processSchedules[0].schedule.startTime)}`;
      } if (this.processSchedules[0].schedule.frequencyType === 'Monthly') {
        return this.formatDayOfMonth(this.processSchedules[0].schedule.frequencyDayOfMonth);
      } if (this.processSchedules[0].schedule.frequencyType === 'Yearly') {
        return `${this.getMonthName(this.processSchedules[0].processSchedule.frequencyMonthOfYear)}
          ${this.formatDayOfMonth(this.processSchedules[0].schedule.frequencyDayOfMonth)}
          ${this.formattedTime(this.processSchedules[0].schedule.startTime)}`;
      }
      return '';
    },
  },
  methods: {
    formatDayOfMonth(day) {
      const parsedDay = parseInt(day, 10); // Specify radix 10 to avoid unexpected behavior
      let suffix = 'th';
      if (parsedDay === 1 || parsedDay === 21 || parsedDay === 31) {
        suffix = 'st';
      } else if (parsedDay === 2 || parsedDay === 22) {
        suffix = 'nd';
      } else if (parsedDay === 3 || parsedDay === 23) {
        suffix = 'rd';
      }
      return `${parsedDay}${suffix}`;
    },
    getMonthName(monthNum) {
      const date = new Date();
      date.setMonth(monthNum - 1);
      return date.toLocaleString('default', { month: 'long' });
    },
    viewNearMatchProcessingDetails() {
      this.$emit('viewNearMatchProcessingDetails');
      this.hideNotification();
    },
    initialData() {
      return {
        loading: false,
        loadingError: false,

        processSchedules: [],
      };
    },
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;
        
        if ((this.activeModule && this.activeModule.name) === 'personId') {
          const res = await axios.get('process-schedules/list/pid');
          this.processSchedules = res.data;
        } else if ((this.activeModule && this.activeModule.name) === 'directMatch') {
          const res = await axios.get('process-schedules/list/dm');
          this.processSchedules = res.data;
        } else if ((this.activeModule && this.activeModule.name) === 'locationID') {
          const res = await axios.get('process-schedules/list/loc');
          this.processSchedules = res.data;
        } else {
          const res = await axios.get('process-schedules');
          this.processSchedules = res.data;
        }      
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    vertical-align: middle
  table td
    vertical-align: middle
  table tr.disabled
    color: #C4C5C6
    cursor: not-allowed
  a.disabled
    color: #E6E6E6
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .header
    display: flex
    align-items: center
  .icon-right
    margin-left: auto
    font-size: 12px
  .last-child
    font-size: 12px
    text-align: right
    width: 1%
    white-space: nowrap
    padding-right: 4rem
  .left-padding
    padding-left: 1.5rem
  .left-right-padding
    padding: 0 0.75rem
  .no-border
    border: 0
  .pipeline-border
    border-left: 1px solid #DEE2E6
  .notification-type
    display: flex
    border-bottom: none
    padding: 1rem 0
  .rule
    font-size: 12px
    color: #666
  .top-padding
    padding-top: 2rem
  .center
    padding: 5rem
    align-items: center
    justify-content: center
    flex-direction: column
  .no-margin
    margin-bottom: 0
    vertical-align: middle
  .exception-text
    font-size: 17.5px
  .exception-icon
    pointer-events: none !important
</style>
